import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Receipt, UserPlus, Check, X, Eye } from 'lucide-react';
import { Button, Card, Badge, Input, Tabs, TabsList, TabsTrigger, toast } from '@employer-on-record/ui';
import type { LeaveRequestDTO, ReimbursementClaimDTO } from '@employer-on-record/shared';
import { leaveApi, reimbursementsApi, companiesApi, employeeRequestsApi } from '@/services/api';
import type { EmployeeRequestDTO } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { PageHead } from '@/components/PageHead';
import { Avatar } from '@/components/Avatar';
import { ApprovalProgress } from '@/components/ApprovalProgress';
import { LeaveDetailModal } from '@/components/modals/LeaveDetailModal';
import { ReimbursementDetailModal } from '@/components/modals/ReimbursementDetailModal';
import { formatIDR, formatNumber, formatDateRange, formatDateShort, LABEL_BY_LEAVE_TYPE, LABEL_BY_REIMBURSEMENT_CATEGORY, LABEL_BY_REIMBURSEMENT_STATUS, TONE_BY_LEAVE_STATUS, TONE_BY_REIMBURSEMENT_STATUS } from '@/lib/utils';

type Tab = 'employee' | 'leave' | 'reimbursement';

const REQUEST_STATUS_TONE = {
	pending: 'amber',
	approved: 'green',
	rejected: 'red'
} as const;

export default function AllRequestsPage() {
	const qc = useQueryClient();
	const role = useAuthStore((s) => s.user?.role);
	const isSuperAdmin = role === 'super_admin';

	const [tab, setTab] = useState<Tab>('employee');
	const [companyId, setCompanyId] = useState<string>('');
	const [detailLeave, setDetailLeave] = useState<LeaveRequestDTO | null>(null);
	const [detailReimb, setDetailReimb] = useState<ReimbursementClaimDTO | null>(null);

	const { data: companies } = useQuery({ queryKey: ['admin-companies'], queryFn: () => companiesApi.list() });
	const { data: employeeRequests } = useQuery({
		queryKey: ['admin-all-employee-requests', companyId],
		queryFn: () => employeeRequestsApi.list(companyId || undefined),
		enabled: tab === 'employee'
	});
	const { data: leaves } = useQuery({
		queryKey: ['admin-all-leave', companyId],
		queryFn: () => leaveApi.list(companyId || undefined),
		enabled: tab === 'leave'
	});
	const { data: reimb } = useQuery({
		queryKey: ['admin-all-reimb', companyId],
		queryFn: () => reimbursementsApi.list(companyId || undefined),
		enabled: tab === 'reimbursement'
	});

	const approveRequest = useMutation({
		mutationFn: (reqId: string) => employeeRequestsApi.approve(reqId),
		onSuccess: (res) => {
			const pwd = res.data.initialPassword;
			toast(`Approved · ${res.data.employee.fullName}${pwd ? ` · password: ${pwd}` : ''}`);
			qc.invalidateQueries({ queryKey: ['admin-all-employee-requests'] });
			qc.invalidateQueries({ queryKey: ['admin-company-employees'] });
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	const rejectRequest = useMutation({
		mutationFn: ({ reqId, reason }: { reqId: string; reason: string }) => employeeRequestsApi.reject(reqId, reason),
		onSuccess: () => {
			toast('Request rejected');
			qc.invalidateQueries({ queryKey: ['admin-all-employee-requests'] });
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	const reimburse = useMutation({
		mutationFn: (id: string) => reimbursementsApi.reimburse(id),
		onSuccess: () => {
			toast('Marked as reimbursement paid');
			qc.invalidateQueries({ queryKey: ['admin-all-reimb'] });
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	const employeeItems = employeeRequests?.data ?? [];
	const leaveItems = leaves?.data ?? [];
	const reimbItems = reimb?.data ?? [];

	return (
		<>
			<PageHead
				title="All requests"
				subtitle="Approve HR-submitted employee requests, and review leave & reimbursement across every client company"
			/>

			<LeaveDetailModal open={!!detailLeave} onClose={() => setDetailLeave(null)} request={detailLeave} />
			<ReimbursementDetailModal open={!!detailReimb} onClose={() => setDetailReimb(null)} claim={detailReimb} />

			<Card>
				<div className="px-5 pt-1 flex items-center gap-3">
					<Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
						<TabsList>
							<TabsTrigger value="employee"><UserPlus className="h-3 w-3 mr-1.5" /> Employee</TabsTrigger>
							<TabsTrigger value="leave"><Calendar className="h-3 w-3 mr-1.5" /> Leave</TabsTrigger>
							<TabsTrigger value="reimbursement"><Receipt className="h-3 w-3 mr-1.5" /> Reimbursement</TabsTrigger>
						</TabsList>
					</Tabs>
					<div className="ml-auto pb-2 pr-5 flex items-center gap-2">
						<label className="text-[12px] text-muted-foreground">Filter by company</label>
						<select
							value={companyId}
							onChange={(e) => setCompanyId(e.target.value)}
							className="border rounded-md px-2 py-1 text-[13px] bg-white"
						>
							<option value="">All companies</option>
							{(companies?.data ?? []).map((c) => (
								<option key={c.id} value={c.id}>{c.name}</option>
							))}
						</select>
					</div>
				</div>

				{tab === 'employee' && (
					<table className="w-full text-[13px]">
						<thead>
							<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
								<th className="text-left px-4 py-2.5 font-semibold">Employee</th>
								<th className="text-left px-4 py-2.5 font-semibold">Department / Position</th>
								<th className="text-left px-4 py-2.5 font-semibold">Start</th>
								<th className="text-left px-4 py-2.5 font-semibold">Requested by</th>
								<th className="text-left px-4 py-2.5 font-semibold">Status</th>
								<th className="px-4 py-2.5" />
							</tr>
						</thead>
						<tbody>
							{employeeItems.length === 0 && (
								<tr><td colSpan={6} className="text-center text-muted-foreground p-10">No employee requests yet — HR submits them here for approval.</td></tr>
							)}
							{employeeItems.map((r) => (
								<RequestRow
									key={r.id}
									req={r}
									canDecide={isSuperAdmin}
									onApprove={() => approveRequest.mutate(r.id)}
									onReject={(reason) => rejectRequest.mutate({ reqId: r.id, reason })}
									isPending={approveRequest.isPending || rejectRequest.isPending}
								/>
							))}
						</tbody>
					</table>
				)}

				{tab === 'leave' && (
					<table className="w-full text-[13px]">
						<thead>
							<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
								<th className="text-left px-4 py-2.5 font-semibold">Employee</th>
								<th className="text-left px-4 py-2.5 font-semibold">Type</th>
								<th className="text-left px-4 py-2.5 font-semibold">Date range</th>
								<th className="text-left px-4 py-2.5 font-semibold">Days</th>
								<th className="text-left px-4 py-2.5 font-semibold">Approval</th>
								<th className="text-left px-4 py-2.5 font-semibold">Status</th>
								<th className="px-4 py-2.5" />
							</tr>
						</thead>
						<tbody>
							{leaveItems.length === 0 && (
								<tr><td colSpan={7} className="text-center text-muted-foreground p-10">No leave requests.</td></tr>
							)}
							{leaveItems.map((r) => (
								<tr key={r.id} className="border-b last:border-0">
									<td className="px-4 py-3.5">
										<div className="flex items-center gap-2.5">
											<Avatar name={r.employee?.fullName || 'Unknown'} />
											<div className="font-medium">{r.employee?.fullName}</div>
										</div>
									</td>
									<td className="px-4 py-3.5">{LABEL_BY_LEAVE_TYPE[r.type as keyof typeof LABEL_BY_LEAVE_TYPE]}</td>
									<td className="px-4 py-3.5">{formatDateRange(r.startDate, r.endDate)}</td>
									<td className="px-4 py-3.5 tabular">{formatNumber(r.days)}d</td>
									<td className="px-4 py-3.5"><ApprovalProgress approvals={r.approvals ?? []} currentLevel={r.currentLevel} /></td>
									<td className="px-4 py-3.5"><Badge tone={TONE_BY_LEAVE_STATUS[r.status]}>{r.status}</Badge></td>
									<td className="px-4 py-3.5 text-right"><Button variant="ghost" size="sm" onClick={() => setDetailLeave(r)}><Eye className="h-3 w-3" /> View</Button></td>
								</tr>
							))}
						</tbody>
					</table>
				)}

				{tab === 'reimbursement' && (
					<table className="w-full text-[13px]">
						<thead>
							<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
								<th className="text-left px-4 py-2.5 font-semibold">Employee</th>
								<th className="text-left px-4 py-2.5 font-semibold">Category</th>
								<th className="text-left px-4 py-2.5 font-semibold">Description</th>
								<th className="text-right px-4 py-2.5 font-semibold">Amount</th>
								<th className="text-left px-4 py-2.5 font-semibold">Approval</th>
								<th className="text-left px-4 py-2.5 font-semibold">Status</th>
								<th className="px-4 py-2.5" />
							</tr>
						</thead>
						<tbody>
							{reimbItems.length === 0 && (
								<tr><td colSpan={7} className="text-center text-muted-foreground p-10">No reimbursement claims.</td></tr>
							)}
							{reimbItems.map((r) => (
								<tr key={r.id} className="border-b last:border-0">
									<td className="px-4 py-3.5">
										<div className="flex items-center gap-2.5">
											<Avatar name={r.employee?.fullName || 'Unknown'} />
											<div className="font-medium">{r.employee?.fullName}</div>
										</div>
									</td>
									<td className="px-4 py-3.5">{LABEL_BY_REIMBURSEMENT_CATEGORY[r.category as keyof typeof LABEL_BY_REIMBURSEMENT_CATEGORY]}</td>
									<td className="px-4 py-3.5">{r.description}</td>
									<td className="px-4 py-3.5 text-right tabular font-semibold">{formatIDR(Number(r.amount))}</td>
									<td className="px-4 py-3.5"><ApprovalProgress approvals={r.approvals ?? []} currentLevel={r.currentLevel} /></td>
									<td className="px-4 py-3.5"><Badge tone={TONE_BY_REIMBURSEMENT_STATUS[r.status]}>{LABEL_BY_REIMBURSEMENT_STATUS[r.status] ?? r.status}</Badge></td>
									<td className="px-4 py-3.5 text-right">
										<div className="inline-flex gap-1.5 justify-end">
											<Button variant="ghost" size="sm" onClick={() => setDetailReimb(r)}><Eye className="h-3 w-3" /> View</Button>
											{isSuperAdmin && r.status === 'sent_to_paid' && (
												<Button size="sm" onClick={() => reimburse.mutate(r.id)} disabled={reimburse.isPending}><Check className="h-3 w-3" /> Reimbursement paid</Button>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Card>
		</>
	);
}

interface RequestRowProps {
	req: EmployeeRequestDTO;
	canDecide: boolean;
	onApprove: () => void;
	onReject: (reason: string) => void;
	isPending: boolean;
}

function RequestRow({ req, canDecide, onApprove, onReject, isPending }: RequestRowProps) {
	const [rejecting, setRejecting] = useState(false);
	const [reason, setReason] = useState('');

	useEffect(() => {
		if (req.status !== 'pending') {
			setRejecting(false);
			setReason('');
		}
	}, [req.status]);

	return (
		<tr className="border-b last:border-0 align-top">
			<td className="px-4 py-3.5">
				<div className="flex items-center gap-2.5">
					<Avatar name={req.fullName} />
					<div>
						<div className="font-medium">{req.fullName}</div>
						<div className="text-[11px] text-muted-foreground">{req.employeeNumber} · {req.email}</div>
					</div>
				</div>
			</td>
			<td className="px-4 py-3.5 text-muted-foreground">
				<div>{req.department || '—'}</div>
				<div className="text-[11px]">{req.position || '—'}</div>
			</td>
			<td className="px-4 py-3.5 text-muted-foreground">{formatDateShort(req.startDate)}</td>
			<td className="px-4 py-3.5 text-muted-foreground">
				<div>{req.requestedBy?.name || '—'}</div>
				<div className="text-[11px]">{formatDateShort(req.createdAt)}</div>
			</td>
			<td className="px-4 py-3.5">
				<Badge tone={REQUEST_STATUS_TONE[req.status]}>{req.status}</Badge>
				{req.status === 'rejected' && req.rejectionReason && (
					<div className="text-[11px] text-muted-foreground mt-1 max-w-[220px]">Reason: {req.rejectionReason}</div>
				)}
			</td>
			<td className="px-4 py-3.5 text-right">
				{canDecide && req.status === 'pending' && !rejecting && (
					<div className="flex items-center justify-end gap-1.5">
						<Button size="sm" variant="secondary" onClick={() => setRejecting(true)} disabled={isPending}><X className="h-3 w-3" /> Reject</Button>
						<Button size="sm" onClick={onApprove} disabled={isPending}><Check className="h-3 w-3" /> Approve</Button>
					</div>
				)}
				{canDecide && req.status === 'pending' && rejecting && (
					<div className="flex items-center justify-end gap-1.5">
						<Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="w-44" />
						<Button size="sm" variant="secondary" onClick={() => { setRejecting(false); setReason(''); }} disabled={isPending}>Cancel</Button>
						<Button size="sm" onClick={() => reason.trim() && onReject(reason.trim())} disabled={isPending || !reason.trim()}>Confirm reject</Button>
					</div>
				)}
			</td>
		</tr>
	);
}
